import type { KitchenCatalog, KitchenConfig, KitchenExportRequest } from '@cfg/shared';
import { analytics } from './analytics';

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = (await response.json()) as { message?: string };
      message = body.message ?? message;
    } catch {
      // not JSON
    }
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export function fetchCatalog(): Promise<KitchenCatalog> {
  return request<KitchenCatalog>('/v1/kitchen/catalog');
}

export async function saveConfiguration(
  config: KitchenConfig,
): Promise<{ code: string; url: string }> {
  const result = await request<{ code: string; url: string }>('/v1/configurations', {
    method: 'POST',
    body: JSON.stringify({ product: 'kitchen', payload: config }),
  });
  analytics.track('share_created', { scene: config.sceneKey });
  return result;
}

export async function loadConfiguration(code: string): Promise<KitchenConfig> {
  const result = await request<{ product: string; payload: KitchenConfig }>(
    `/v1/configurations/${encodeURIComponent(code)}`,
  );
  if (result.product !== 'kitchen') throw new ApiError(404, 'Not a kitchen configuration');
  return result.payload;
}

export async function exportPdf(
  body: KitchenExportRequest,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_URL}/v1/kitchen/exports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text();
    let message = response.statusText;
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? message;
    } catch {
      // not JSON
    }
    throw new ApiError(response.status, message);
  }
  const code = response.headers.get('X-Configuration-Code') ?? 'kitchen';
  analytics.track('export_created', {
    scenes: body.sceneKeys.length,
    lightings: body.lightings.join('+'),
  });
  const name = (body.project.projectName || 'kitchen').replace(/[\\/:*?"<>|]+/g, '').slice(0, 60);
  return { blob: await response.blob(), filename: `${name}-${code}.pdf` };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
